import { ReactNode } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { Button, ButtonToolbar } from '@/shared/components/Button';
import styled from 'styled-components';
import { colorAccent, colorBackground, colorBlue, colorIcon, colorRed, colorText, colorWhite, colorYellow } from '@/utils/palette';
import { flexFlow, left, paddingLeft, paddingRight, right } from '@/utils/directions';
import { LoadingSpinner } from './Loading';

const color = 'primary';

interface FormModalProps {
    isOpen: boolean;
    showDelete: boolean;
    loading: boolean;
    title: string;
    body: ReactNode;
    handleSave: () => void;
    handleDelete: () => void;
    toggle: () => void;
    // When true the Save button is disabled (e.g. a media upload is in flight).
    saveDisabled?: boolean;
    // Shown above the footer to explain why Save is disabled.
    saveDisabledHint?: string;
}

const FormModal = ({ isOpen, showDelete, loading, title, body, toggle, handleSave, handleDelete, saveDisabled = false, saveDisabledHint }: FormModalProps) => {
    const onDelete = () => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            handleDelete();
        }
    };

    return (
        <StyledModal show={isOpen} color={color} colored={false} header={false} backdrop="static" keyboard={false} size="lg">
            <ModalHeader>
                <ModalTitle>{title}</ModalTitle>
            </ModalHeader>
            {loading ? (
                <div className="d-flex justify-content-center">
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <ModalBody>{body}</ModalBody>
                    {saveDisabled && saveDisabledHint && (
                        <div className="d-flex justify-content-center align-items-center" style={{ gap: 8, color: colorBlue, marginTop: 8 }}>
                            <LoadingSpinner />
                            <span>{saveDisabledHint}</span>
                        </div>
                    )}
                    <ModalFooter>
                        <Button variant="secondary" onClick={toggle}>
                            Cancel
                        </Button>
                        <Button variant={color} onClick={handleSave} disabled={saveDisabled}>
                            Save
                        </Button>
                        {showDelete && (
                            <Button variant={'danger'} onClick={onDelete}>
                                Delete
                            </Button>
                        )}
                    </ModalFooter>
                </>
            )}
        </StyledModal>
    );
};

export default FormModal;

const ModalFooter = styled(ButtonToolbar)`
    margin-top: 20px;
    display: flex;
    justify-content: center;
    margin-bottom: 0;

    button {
        min-width: 100px;
        padding: 4px 25px;
        margin-bottom: 0;
        display: inline-block;
    }

    flex-flow: ${flexFlow} !important;
`;

const ModalTitle = styled.h4`
    margin-top: 10px;
    margin-bottom: 15px;
    font-weight: 700;

    &:first-child {
        margin-top: 0;
    }
`;

const ModalCloseButton = styled.button`
    position: absolute;
    top: 15px;
    ${right}: 15px;
    font-size: 14px;
    width: 14px;
    height: 14px;
    cursor: pointer;
    padding: 0;
    border: none;
    background: transparent;
    color: ${colorIcon};
`;

const ModalTitleIcon = styled.span`
    width: 24px;
    height: 24px;
    font-size: 24px;
`;

const ModalHeader = styled.div``;
const ModalBody = styled.div``;

const getColor = (color: string) => {
    switch (color) {
        case 'primary':
            return colorBlue;
        case 'success':
            return colorAccent;
        case 'warning':
            return colorYellow;
        case 'danger':
            return colorRed;

        default:
            return colorText;
    }
};

const StyledModal = styled(BootstrapModal).withConfig({
    shouldForwardProp: (prop: any) => !['colored', 'header'].includes(prop),
})`
    .modal-dialog {
        // max-width: 385px;
    }

    .modal-content {
        border-radius: 0;
        border: none;
        box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
        position: relative;
        padding: 50px 40px 60px 40px;
        text-align: center;
        background-color: ${colorBackground};
        color: ${colorText};
    }

    ${ModalTitleIcon} {
        color: ${(props) => getColor(props.color)};
    }

    ${(props) =>
        props.colored &&
        `
    color: ${colorWhite};

    ${ModalTitle},
    ${ModalTitleIcon},
    ${ModalCloseButton} {
      color: ${colorWhite};
    }

    ${ModalFooter} {

      button:first-child {
        color: ${colorWhite};
        background-color: rgba(255, 255, 255, 0.3);
        border-color: ${colorWhite};

        &:before {
          background-color: rgba(255, 255, 255, 0.2);
        }
      }

      button:last-child {
        border-color: white;
        color: ${colorWhite};
      }
    }

    .modal-content {
      color: ${colorWhite};
      background-color: ${getColor(props.color)};
    }
  `}

    ${(props) =>
        props.header &&
        `
    
    .modal-dialog {
      max-width: 520px;
    }

    .modal-content {
      padding: 0;
      text-align: ${left(props)};
    }
    
    ${ModalTitle} {
      color: ${colorWhite};
    }

    ${ModalHeader} {
      color: ${colorWhite};
      padding: 15px 20px;
      position: relative;
    }

    ${ModalTitle} {
      margin: 0;
      font-weight: 300;
    }

    ${ModalCloseButton} {
      color: ${colorWhite};
      top: calc(50% - 8px);
    }

    ${ModalBody} {
      padding-top: 25px;
      padding-bottom: 20px;
      ${paddingRight(props)}: 40px;
      ${paddingLeft(props)}: 20px;
    }

    ${ModalFooter} {
      margin-bottom: 40px;
      justify-content: flex-end;
      ${paddingRight(props)}: 20px;
    }

    ${ModalHeader} {
      background-color: ${getColor(props.color)};
    }
  `}
`;
